import { useId, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cx } from './cx';
import { Pagination, type PaginationProps } from './Pagination';
import { Skeleton } from './Skeleton';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** Cell content; defaults to String(row[id]). */
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  /** Value compared when sorting client-side; defaults to row[id]. */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  align?: 'start' | 'end' | 'center';
  /** Hide on narrow screens (content still available on the detail page). */
  hideBelow?: 'md' | 'lg';
  /** This column names the row (<th scope="row">), e.g. the summary. */
  rowHeader?: boolean;
  className?: string;
}

export interface DataTableProps<T> {
  /** Visible caption (or sr-only with `hideCaption`) — names the table and its scroll region. */
  caption: ReactNode;
  hideCaption?: boolean;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string | number;
  /** Controlled sort; omit for internal state. */
  sort?: SortState | null;
  defaultSort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /**
   * 'client' sorts and paginates `rows` here; 'server' shows `rows` as-is (the caller fetched the
   * page for `sort` + `pagination`).
   */
  mode?: 'client' | 'server';
  pagination?: Omit<PaginationProps, 'total'> & { total?: number };
  loading?: boolean;
  loadingRows?: number;
  empty?: ReactNode;
  rowStyle?: ((row: T) => CSSProperties | undefined) | undefined;
  /** i18n: spoken with sortable headers. */
  sortHint?: { asc: string; desc: string; none: string };
  className?: string;
}

const HIDE = { md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' } as const;
const ALIGN = { start: 'text-start', end: 'text-end', center: 'text-center' } as const;

type SortValue = string | number | Date | boolean | null | undefined;

function compare(a: SortValue, b: SortValue): number {
  if ((a === null || a === undefined) && (b === null || b === undefined)) return 0;
  if (a === null || a === undefined) return 1; // empty values last
  if (b === null || b === undefined) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return display(a).localeCompare(display(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Default cell text for a raw value. */
function display(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/**
 * Data table (legacy view_all_bug_page / manage lists). Sortable columns are buttons inside the
 * header with aria-sort on the sorted <th>; the table sits in a focusable, labelled scroll region
 * so keyboard users can scroll it on small screens (WCAG 1.4.10, 2.1.1).
 * Cycle per column: ascending → descending → unsorted.
 */
export function DataTable<T>({
  caption,
  hideCaption,
  columns,
  rows,
  rowKey,
  sort: sortProp,
  defaultSort = null,
  onSortChange,
  mode = 'client',
  pagination,
  loading,
  loadingRows = 5,
  empty = 'No records found.',
  rowStyle,
  sortHint = { asc: 'sorted ascending', desc: 'sorted descending', none: 'sortable' },
  className,
}: DataTableProps<T>) {
  const id = `t${useId().replace(/:/g, '')}`;
  const [innerSort, setInnerSort] = useState<SortState | null>(defaultSort);
  const sort = sortProp !== undefined ? sortProp : innerSort;

  const setSort = (columnId: string) => {
    const next: SortState | null =
      sort?.columnId !== columnId
        ? { columnId, direction: 'asc' }
        : sort.direction === 'asc'
          ? { columnId, direction: 'desc' }
          : null;
    if (sortProp === undefined) setInnerSort(next);
    onSortChange?.(next);
    if (mode === 'client' && pagination && pagination.page !== 1) pagination.onPageChange(1);
  };

  const visible = useMemo(() => {
    if (mode === 'server') return rows;
    let out = [...rows];
    const col = sort && columns.find((c) => c.id === sort.columnId);
    if (sort && col) {
      const value =
        col.sortValue ?? ((r: T) => (r as Record<string, unknown>)[col.id] as SortValue);
      out.sort((a, b) => compare(value(a), value(b)) * (sort.direction === 'asc' ? 1 : -1));
    }
    if (pagination) {
      const start = (pagination.page - 1) * pagination.pageSize;
      out = out.slice(start, start + pagination.pageSize);
    }
    return out;
  }, [rows, sort, columns, mode, pagination]);

  const total = pagination?.total ?? rows.length;

  return (
    <div className={cx('flex min-w-0 flex-col gap-3', className)}>
      <div
        role="region"
        aria-labelledby={`${id}-cap`}
        tabIndex={0}
        className="relative min-w-0 overflow-x-auto border border-border-widget bg-surface"
      >
        <table
          aria-busy={loading ? true : undefined}
          className={cx('w-full border-collapse text-sm', rowStyle && 'on-tint')}
        >
          <caption
            id={`${id}-cap`}
            className={cx(hideCaption ? 'sr-only' : 'px-3 py-2 text-start font-semibold text-ink')}
          >
            {caption}
          </caption>
          <thead className="bg-surface-sunken text-ink-muted">
            <tr>
              {columns.map((c) => {
                const dir = sort?.columnId === c.id ? sort.direction : null;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={
                      c.sortable && dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cx(
                      'border-b border-border-widget px-3 py-2 font-semibold whitespace-nowrap',
                      ALIGN[c.align ?? 'start'],
                      c.hideBelow && HIDE[c.hideBelow],
                      c.className,
                    )}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        onClick={() => setSort(c.id)}
                        className={cx(
                          'inline-flex items-center gap-1 font-semibold hover:text-ink',
                          dir && 'text-ink',
                        )}
                      >
                        {c.header}
                        <span className="sr-only">
                          ,{' '}
                          {dir === 'asc'
                            ? sortHint.asc
                            : dir === 'desc'
                              ? sortHint.desc
                              : sortHint.none}
                        </span>
                        {dir === 'asc' ? (
                          <ArrowUp aria-hidden="true" className="size-3.5" />
                        ) : dir === 'desc' ? (
                          <ArrowDown aria-hidden="true" className="size-3.5" />
                        ) : (
                          <ArrowUpDown aria-hidden="true" className="size-3.5 opacity-50" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: loadingRows }, (_, i) => (
                <tr key={`sk-${i}`} className="border-b border-border last:border-b-0">
                  {columns.map((c) => (
                    <td key={c.id} className={cx('px-3 py-2.5', c.hideBelow && HIDE[c.hideBelow])}>
                      <Skeleton width={c.rowHeader ? '80%' : '60%'} />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-ink-muted">
                  {empty}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={rowKey(row)}
                  style={rowStyle?.(row)}
                  className="border-b border-border last:border-b-0 even:bg-surface-muted/60 hover:bg-surface-hover"
                >
                  {columns.map((c) => {
                    const content =
                      c.cell?.(row) ?? display((row as Record<string, unknown>)[c.id]);
                    const cls = cx(
                      'px-3 py-2 align-top',
                      ALIGN[c.align ?? 'start'],
                      c.hideBelow && HIDE[c.hideBelow],
                      c.className,
                    );
                    return c.rowHeader ? (
                      <th key={c.id} scope="row" className={cx(cls, 'font-normal')}>
                        {content}
                      </th>
                    ) : (
                      <td key={c.id} className={cls}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {loading && (
          <span role="status" className="sr-only">
            Loading…
          </span>
        )}
      </div>
      {pagination && <Pagination {...pagination} total={total} />}
    </div>
  );
}
